import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { getProjects, getDataByProjectId } from '@/DB/CRUD';
import { Project, DataItem } from '@/lib/constants';
import { stripLotSuffix } from '@/lib/utils';



function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<Project | null>(null);
  const [items, setItems] = useState<DataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'paint_pending' | 'paint_done' | 'load_pending' | 'load_done'>('all');
  const [scannedByFilter, setScannedByFilter] = useState('');

  useEffect(() => {
    async function fetchData() {
      if (!projectId) return;
      
      try {
        setLoading(true);
        
        // Fetch project details
        const projects = await getProjects();
        const foundProject = projects.find(p => p.job_id === projectId);
        
        if (!foundProject) {
          setError('Project not found');
          setLoading(false);
          return;
        }
        
        setProject(foundProject);
        
        // Fetch project items
        const data = await getDataByProjectId(projectId);
        setItems(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch project data');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [projectId]);

  const formatScanTime = (scanTime?: string) => {
    if (!scanTime) return '-';
    const date = new Date(scanTime);
    return date.toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const paintProgress = project && project.scan_total > 0 ? ((project.paint_scan_count ?? 0) / project.scan_total) * 100 : 0;
  const loadProgress = project && project.scan_total > 0 ? ((project.load_scan_count ?? 0) / project.scan_total) * 100 : 0;

  const getProgressBarColor = (status: string, prog: number) => {
    if (status.toLowerCase() === 'completed' || prog === 100) return 'bg-green-500';
    if (status.toLowerCase() === 'in progress' || status.toLowerCase() === 'in_progress') {
      if (prog > 50) return 'bg-green-500';
      return 'bg-orange-500';
    }
    return 'bg-gray-400';
  };

  const getDisplayStatus = (project: Project) => {
    const paintCount = project.paint_scan_count ?? 0;
    const loadCount = project.load_scan_count ?? 0;
    if (loadCount >= project.scan_total && project.scan_total > 0) return 'Complete';
    if (paintCount > 0 || loadCount > 0) return 'In Progress';
    return 'Pending';
  };

  const displayStatus = project ? getDisplayStatus(project) : 'Pending';

  // Apply filters (AND logic)
  const filteredItems = items.filter(item => {
    const matchesStatus = (() => {
      switch (statusFilter) {
        case 'paint_pending': return !item.paint_scan_time;
        case 'paint_done':    return !!item.paint_scan_time;
        case 'load_pending':  return !item.load_scan_time;
        case 'load_done':     return !!item.load_scan_time;
        default:              return true;
      }
    })();

    const matchesScannedBy =
      scannedByFilter.trim() === '' ||
      (item.paint_scan_operator ?? '').toLowerCase().includes(scannedByFilter.trim().toLowerCase()) ||
      (item.load_scan_operator ?? '').toLowerCase().includes(scannedByFilter.trim().toLowerCase());

    return matchesStatus && matchesScannedBy;
  });

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={8} className="text-center text-gray-500 py-8">
            Loading items...
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={8} className="text-center text-gray-500 py-8">
            {error}
          </TableCell>
        </TableRow>
      );
    }

    if (items.length === 0) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={8} className="text-center text-gray-500 py-8">
            No items available
          </TableCell>
        </TableRow>
      );
    }

    if (filteredItems.length === 0) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={8} className="text-center text-gray-500 py-8">
            No items match the current filters
          </TableCell>
        </TableRow>
      );
    }

    return filteredItems.map((item, index) => (
      <TableRow key={item.id || index} className="hover:bg-transparent">
        <TableCell className="font-mono">{item.serial}</TableCell>
        <TableCell>{item.mark ?? '—'}</TableCell>
        <TableCell>{item.stock}</TableCell>
        <TableCell>{item.length ?? '—'}</TableCell>
        <TableCell className="text-gray-600">
          {item.paint_scan_operator ?? <span className="text-yellow-600">Pending</span>}
        </TableCell>
        <TableCell className="text-gray-600">
          {formatScanTime(item.paint_scan_time ?? undefined)}
        </TableCell>
        <TableCell className="text-gray-600">
          {item.load_scan_operator ?? <span className="text-yellow-600">Pending</span>}
        </TableCell>
        <TableCell className="text-gray-600">
          {formatScanTime(item.load_scan_time ?? undefined)}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Card className="rounded-t-lg shadow-md m-5 border-0">
        <CardHeader className="bg-[#2c3e50] text-white py-10 px-5 flex flex-row justify-between items-center rounded-t-lg">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="secondary"
              className="bg-[#34495e] hover:bg-[#3d566e] text-white"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Projects
            </Button>
            <div className="flex flex-col">
              <CardTitle className="text-4xl font-bold font-['Arial'] text-white m-0">
                {project ? stripLotSuffix(project.job_name, project.lot_number) : 'Project'}
              </CardTitle>
              {project?.lot_number && (
                <span className="text-sm text-gray-300 mt-1">
                  Lot {project.lot_number}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-5">
          <div className="mb-6">
            <div className="flex flex-col gap-2">
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Paint: {project?.paint_scan_count ?? 0}/{project?.scan_total ?? 0}</span>
                  <span>{paintProgress.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`${getProgressBarColor(displayStatus, Math.round(paintProgress))} h-4 rounded-full transition-all`}
                    style={{ width: `${paintProgress}%` }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Load: {project?.load_scan_count ?? 0}/{project?.scan_total ?? 0}</span>
                  <span>{loadProgress.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className={`${getProgressBarColor(displayStatus, Math.round(loadProgress))} h-4 rounded-full transition-all`}
                    style={{ width: `${loadProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <CardTitle className="text-2xl font-bold mb-4 font-['Arial']">Items</CardTitle>

          {/* Filter controls */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#34495e]"
              >
                <option value="all">All</option>
                <option value="paint_pending">Paint Pending</option>
                <option value="paint_done">Paint Done</option>
                <option value="load_pending">Load Pending</option>
                <option value="load_done">Load Done</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-600">Scanned By</label>
              <input
                type="text"
                placeholder="Filter by operator..."
                value={scannedByFilter}
                onChange={e => setScannedByFilter(e.target.value)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#34495e] min-w-[180px]"
              />
            </div>
          </div>
          
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-[#34495e]">
                <TableHead className="bg-[#34495e] text-white">Serial</TableHead>
                <TableHead className="bg-[#34495e] text-white">Mark</TableHead>
                <TableHead className="bg-[#34495e] text-white">Stock</TableHead>
                <TableHead className="bg-[#34495e] text-white">Length</TableHead>
                <TableHead className="bg-[#34495e] text-white">Paint Operator</TableHead>
                <TableHead className="bg-[#34495e] text-white">Paint Time</TableHead>
                <TableHead className="bg-[#34495e] text-white">Load Operator</TableHead>
                <TableHead className="bg-[#34495e] text-white">Load Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTableContent()}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default ProjectDetail;
